import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart, Bar, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell,
} from "recharts";
import { Clock, Timer, CheckCircle2, ShieldAlert, Loader2 } from "lucide-react";
import { humanCategory } from "@/lib/util";

const SEV_COLOR = {
  critical: "#FF2E93", high: "#FF6B00", medium: "#F1D302",
  low: "#00E5FF", info: "#8B5CF6",
};
const SEV_ORDER = ["critical", "high", "medium", "low", "info"];
const LAYER_COLOR = { exposure: "#00E5FF", vulnerability: "#FF6B00", other: "#8B5CF6" };
const LAYER_LABEL = {
  exposure: "Exposición", vulnerability: "Vulnerabilidad", other: "Otros",
};

function fmtHours(h) {
  if (h === null || h === undefined) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

function fmtPct(p) {
  if (p === null || p === undefined) return "—";
  return `${p.toFixed(1)}%`;
}

function KpiCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="border border-border bg-card/60 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 overline">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="font-display text-4xl mt-2 tracking-tight">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1 font-mono">{sub}</div>}
    </div>
  );
}

function chartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="border border-border bg-card px-3 py-2 text-xs font-mono">
      <div className="uppercase text-muted-foreground text-[10px] tracking-widest">{label}</div>
      <div>MTTR: <span className="text-foreground">{fmtHours(d.mttr_hours)}</span></div>
      <div>MTT-Triage: <span className="text-foreground">{fmtHours(d.mtt_triage_hours)}</span></div>
      <div>Cerrados: <span className="text-foreground">{d.count}</span></div>
      {d.sla_met_pct !== null && d.sla_met_pct !== undefined && (
        <div>SLA cumplido: <span className="text-foreground">{fmtPct(d.sla_met_pct)}</span></div>
      )}
    </div>
  );
}

export default function MTTRDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(
    () => sessionStorage.getItem("cibraxis:mttr:client") || ""
  );

  useEffect(() => {
    api.get("/clients").then(r => setClients(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ period_days: String(period) });
    if (clientId) qs.set("client_id", clientId);
    api.get(`/mttr/summary?${qs.toString()}`)
      .then(r => setData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "No se pudo cargar MTTR"))
      .finally(() => setLoading(false));
  }, [period, clientId]);

  const sevBars = useMemo(() => {
    if (!data?.by_severity) return [];
    return SEV_ORDER
      .filter(s => data.by_severity[s])
      .map(s => ({ label: s.toUpperCase(), color: SEV_COLOR[s], ...data.by_severity[s] }));
  }, [data]);

  const layerBars = useMemo(() => {
    if (!data?.by_layer) return [];
    return Object.entries(data.by_layer).map(([k, v]) => ({
      label: LAYER_LABEL[k] || k, color: LAYER_COLOR[k] || "#8B5CF6", ...v,
    }));
  }, [data]);

  const catBars = useMemo(() => {
    if (!data?.by_category) return [];
    return Object.entries(data.by_category)
      .sort((a, b) => (b[1].count || 0) - (a[1].count || 0))
      .slice(0, 12)
      .map(([k, v]) => ({ label: humanCategory ? humanCategory(k) : k, color: "#00E5FF", ...v }));
  }, [data]);

  const onClient = (id) => {
    setClientId(id);
    if (id) sessionStorage.setItem("cibraxis:mttr:client", id);
    else sessionStorage.removeItem("cibraxis:mttr:client");
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="mttr-dashboard">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline mb-2">MTTR · Analytics</div>
          <h1 className="font-display text-4xl font-medium tracking-tight">Tiempo medio de resolución</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Métricas <code className="text-primary">time_to_triage</code> y <code className="text-primary">time_to_resolve</code>
            {" "}calculadas desde <code>created_at</code>, <code>triaged_at</code> y <code>closed_at</code>.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            data-testid="mttr-client-filter"
            value={clientId}
            onChange={(e) => onClient(e.target.value)}
            className="bg-card border border-border px-3 py-2 text-sm font-mono"
          >
            <option value="">Todos los clientes</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <div className="inline-flex border border-border bg-card/60" data-testid="mttr-period-selector">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                data-testid={`mttr-period-${d}`}
                onClick={() => setPeriod(d)}
                className={`px-3 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${
                  period === d ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
      </header>

      {loading || !data ? (
        <div className="p-8 text-muted-foreground font-mono text-sm flex items-center gap-2" data-testid="mttr-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Calculando MTTR…
        </div>
      ) : (
        <>
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="mttr-kpis">
            <KpiCard
              icon={Clock}
              label="MTTR promedio"
              value={fmtHours(data.mttr_hours)}
              sub={`Sobre ${data.total_closed} findings cerrados`}
              testid="kpi-mttr"
            />
            <KpiCard
              icon={Timer}
              label="MTT-Triage promedio"
              value={fmtHours(data.mtt_triage_hours)}
              sub="Tiempo hasta primer triage"
              testid="kpi-mtt-triage"
            />
            <KpiCard
              icon={CheckCircle2}
              label="SLA cumplido"
              value={fmtPct(data.sla_met_pct)}
              sub="Cerrados dentro del due-date"
              testid="kpi-sla-met"
            />
            <KpiCard
              icon={ShieldAlert}
              label="Findings cerrados"
              value={data.total_closed}
              sub={`Ventana ${data.period_days} días`}
              testid="kpi-closed-count"
            />
          </section>

          <section className="border border-border bg-card/60" data-testid="mttr-by-severity">
            <div className="p-5 border-b border-border">
              <div className="overline">Por severidad</div>
              <h2 className="font-display text-lg font-medium mt-1">MTTR y triage por severidad</h2>
            </div>
            <div className="p-4">
              {sevBars.length === 0 ? (
                <p className="text-sm text-muted-foreground font-mono p-4">Sin datos en el periodo.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={sevBars} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="label" stroke="#888" fontSize={11} />
                    <YAxis stroke="#888" fontSize={11}
                      tickFormatter={(v) => fmtHours(v)}
                      label={{ value: "horas", angle: -90, position: "insideLeft", fill: "#888", fontSize: 10 }} />
                    <Tooltip content={chartTooltip} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                    <Bar dataKey="mttr_hours" name="MTTR">
                      {sevBars.map((e, i) => (
                        <Cell key={i} fill={e.color} />
                      ))}
                    </Bar>
                    <Bar dataKey="mtt_triage_hours" name="Triage" fill="#8B5CF6" />
                  </BarChart>
                </ResponsiveContainer>
              )}
              <div className="mt-3 flex gap-4 text-[10px] font-mono tracking-widest uppercase text-muted-foreground">
                <span><span className="inline-block w-2 h-2 mr-1 align-middle bg-[#FF2E93]" />MTTR (color = severidad)</span>
                <span><span className="inline-block w-2 h-2 mr-1 align-middle bg-[#8B5CF6]" />MTT-Triage</span>
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="border border-border bg-card/60" data-testid="mttr-by-layer">
              <div className="p-5 border-b border-border">
                <div className="overline">Por capa</div>
                <h2 className="font-display text-lg font-medium mt-1">Exposición vs Vulnerabilidad</h2>
              </div>
              <div className="p-4">
                {layerBars.length === 0 ? (
                  <p className="text-sm text-muted-foreground font-mono p-4">Sin datos en el periodo.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={layerBars}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="label" stroke="#888" fontSize={11} />
                      <YAxis stroke="#888" fontSize={11} tickFormatter={(v) => fmtHours(v)} />
                      <Tooltip content={chartTooltip} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                      <Bar dataKey="mttr_hours" name="MTTR">
                        {layerBars.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="border border-border bg-card/60" data-testid="mttr-by-category">
              <div className="p-5 border-b border-border">
                <div className="overline">Por categoría</div>
                <h2 className="font-display text-lg font-medium mt-1">Top categorías (MTTR)</h2>
              </div>
              <div className="p-4">
                {catBars.length === 0 ? (
                  <p className="text-sm text-muted-foreground font-mono p-4">Sin datos en el periodo.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={catBars} layout="vertical" margin={{ left: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis type="number" stroke="#888" fontSize={11} tickFormatter={(v) => fmtHours(v)} />
                      <YAxis type="category" dataKey="label" stroke="#888" fontSize={10} width={130} />
                      <Tooltip content={chartTooltip} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                      <Bar dataKey="mttr_hours" fill="#00E5FF" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </section>

          <section className="border border-border bg-card/60" data-testid="mttr-table">
            <div className="p-5 border-b border-border">
              <div className="overline">Detalle</div>
              <h2 className="font-display text-lg font-medium mt-1">Tabla por severidad</h2>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left overline border-b border-border">
                  <th className="p-3">Severidad</th>
                  <th className="p-3">Cerrados</th>
                  <th className="p-3">MTTR</th>
                  <th className="p-3">MTT-Triage</th>
                  <th className="p-3">SLA cumplido</th>
                </tr>
              </thead>
              <tbody>
                {sevBars.length === 0 && (
                  <tr><td colSpan={5} className="p-4 text-center text-muted-foreground font-mono">Sin datos.</td></tr>
                )}
                {sevBars.map((r) => (
                  <tr key={r.label} className="border-b border-border/50" data-row-hover data-testid={`mttr-row-${r.label.toLowerCase()}`}>
                    <td className="p-3 font-mono text-xs" style={{ color: r.color }}>{r.label}</td>
                    <td className="p-3 font-mono text-xs">{r.count}</td>
                    <td className="p-3 font-mono text-xs">{fmtHours(r.mttr_hours)}</td>
                    <td className="p-3 font-mono text-xs">{fmtHours(r.mtt_triage_hours)}</td>
                    <td className="p-3 font-mono text-xs">{fmtPct(r.sla_met_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}
